// RowActionsMenu — the ⋮ at the end of a register row.
//
// 2026-10-29 (v0.263.0) — Brett: "Shall we have the 3 dots at the end of the
// line". The row tap still opens the item; the ⋮ holds the rest (Edit details,
// Photos, Show on plan, Delete). The list is drawn on the page body so a
// scrolling table or a card's overflow cannot clip it, and closes on a tap
// elsewhere, Escape, scroll or resize.
//
// items: [{ key, label, onSelect, danger?, Icon? }]
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { theme } from '../theme';

const MENU_WIDTH = 190;

export default function RowActionsMenu({ items, label = 'Item actions' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Place under the button, right edges aligned; flip above when it would run
  // off the bottom; never past either side of the window.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const h = menuRef.current ? menuRef.current.offsetHeight : items.length * 40 + 8;
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    const below = r.bottom + 4;
    const top = below + h > window.innerHeight - 8 ? Math.max(8, r.top - 4 - h) : below;
    setPos({ left, top });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  useEffect(() => {
    if (open && pos) menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open, pos]);

  const onMenuKey = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const list = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const i = list.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
    list[next]?.focus();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title="More"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          width: 32, height: 32, padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: open ? theme.bg : 'transparent',
          border: `1px solid ${open ? theme.border : 'transparent'}`,
          borderRadius: 6, cursor: 'pointer', color: theme.textLight,
        }}
      >
        <MoreVertical size={18} aria-hidden="true" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', left: pos ? pos.left : -9999, top: pos ? pos.top : -9999,
            width: MENU_WIDTH, padding: 4,
            background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)', zIndex: 1600,
          }}
        >
          {items.map(({ key, label: text, onSelect, danger, Icon }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onSelect(); }}
              onMouseEnter={(e) => { e.currentTarget.style.background = theme.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                textAlign: 'left', padding: '9px 10px', minHeight: 36,
                background: 'transparent', border: 'none', borderRadius: 6,
                color: danger ? theme.danger : theme.text,
                fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              {Icon && <Icon size={15} aria-hidden="true" />}
              {text}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
